import toast from "react-hot-toast";
import type {
  ChatbotBlock,
  ChatbotFeedbackActionEvent,
} from "blocks/v2/Chatbot/schema";
import React, { useEffect, useMemo, useState } from "react";
import { BotMessage } from "./BotMessage";
import { BotResponse } from "./BotResponse";
import {
  UserMsg,
  BotMsg,
  isBotMsg,
  OpenAiUsage,
  FuncMsg,
  Msg as BaseMsg,
} from "../types";
import { useChatConfig } from "./useChatConfig";
import { UserMessage } from "./UserMessage";
import { UserPrompt } from "./UserPrompt";
import { slug } from "cuid";
import { useChatCompletion } from "./useChatCompletion";
import { BotActions } from "./BotActions";
import { BotMarkdown } from "./BotMarkdown";
import { CopyBotMessage } from "./CopyBotMessage";
import { RunZapBotMessage } from "./RunZapBotMessage";
import { useTriggerChatbotBlockAction } from "./BotActionsContext";
import { BotFeedback } from "./BotFeedback";
import { Icon } from "@zapier/design-system";
import { ConsumerProvider } from "lib/context/consumer-provider";
import { buildBlockDisplayStyle } from "../lib/buildBlockDisplayStyle";
import ScrollToBottom, {
  useAtTop,
  useAtBottom,
  useSticky,
  useScrollToBottom,
} from "react-scroll-to-bottom";
import {
  SYSTEM_MSG_DATASOURCE_AVAILABLE,
  generateSystemMsgNoResultsStatic,
  SYSTEM_MSG_DATASOURCE_NO_RESULTS_GENERATE,
} from "../promptTemplates";
import { cn } from "utils/cn";
import { useChatbotSessionId } from "../ChatbotSessionIdContext";
import { useEmbed } from "lib/context/embed-context";

type Msg = BaseMsg & {
  id: string;
  message?: string;
  hidden?: boolean;
};

const StyledChatbot: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div"> & {
    $blockHeights: { height: string; maxHeight: string };
    $isPopup: boolean;
  }
> = ({ $blockHeights, $isPopup, ...props }) => (
  <div
    {...props}
    style={{
      height: $blockHeights.height,
      maxHeight: $blockHeights.maxHeight,
    }}
    className={cn(
      "flex h-full w-full flex-col justify-end gap-1",
      {
        absolute: $isPopup,
        relative: !$isPopup,
      },
      props.className
    )}
  />
);

// Unfortunately, we need to use a custom button here intead of the DS button, because the neutral
// DS button is actually a transparent background, and is hard to override with styled-components
const StyledJumpButton: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"button"> & {
    $isHidden: boolean;
    $isAtBottom: boolean;
  }
> = ({ $isHidden, $isAtBottom, ...props }) => (
  <button
    {...props}
    disabled={$isAtBottom || props.disabled}
    className={cn(
      "absolute bottom-0 mb-5 inline-flex cursor-pointer rounded-full border border-solid border-zi-gray bg-zi-superLightGray px-5 py-2.5 font-sans text-base font-medium shadow-sm transition-opacity focus-within:border-zi-primary focus-within:outline-none focus-within:ring-1 focus-within:ring-zi-primary",
      {
        "pointer-events-none opacity-0": $isHidden,
        "pointer-events-auto opacity-100": !$isHidden,
      },
      props.className
    )}
  />
);

const StyledOverlay: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div"> & {
    $scrollPosition: { isAtTop: boolean; isAtBottom: boolean };
  }
> = ({ $scrollPosition, ...props }) => {
  const { noBackground } = useEmbed();
  return (
    <div
      {...props}
      className={cn(
        "before:content-[' '] after:content-[' '] pointer-events-none absolute inset-0 before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-6 before:bg-gradient-to-t before:from-transparent before:transition-opacity after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-6 after:bg-gradient-to-b after:from-transparent after:transition-opacity",
        {
          "before:opacity-0": $scrollPosition.isAtTop,
          "before:opacity-100": !$scrollPosition.isAtTop,
          "before:to-zi-pageBackground after:to-zi-pageBackground":
            !noBackground,
          "before:to-transparent after:to-transparent": noBackground,
        },
        props.className
      )}
    />
  );
};

const StyledJumpButtonWrapper: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div">
> = (props) => (
  <div
    {...props}
    className={cn(
      "pointer-events-none sticky inset-0 flex justify-center",
      props.className
    )}
  />
);
export function LiveChatbotPrompt({
  config,
  isDisabled = false,
  onUsage,
  isInPopup = false,
}: {
  config: ChatbotBlock["config"];
  isDisabled?: boolean;
  onUsage: (usage: OpenAiUsage) => void;
  isInPopup?: boolean;
}) {
  const {
    blockId,
    chatbotName,
    dataSources,
    dataSourcesNoResultsMessage,
    dataSourcesNoResultsMode,
    model,
    placeholder,
    user,
  } = useChatConfig(config);

  const [liveBotResponse, setLiveBotResponse] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);

  const userPromptProps = useMemo(
    () => ({
      isDisabled: false,
      chatbotName,
      placeholder,
      messages,
      blockId,
      user,
      onChatComplete(userMsg: UserMsg, botMsg: BotMsg) {
        setIsSubmitting(false);
        setMessages((prev) => [
          ...prev,
          {
            ...botMsg,
            id: slug(),
          },
          {
            id: slug(),
            role: "user",
            content: "",
          },
        ]);
      },
      onLiveBotResponseUpdated(latestBotMsg: string) {
        setLiveBotResponse(latestBotMsg);
      },
      onSubmitPrompt(userMsg: UserMsg) {
        setIsSubmitting(true);

        setMessages((prev) => [
          ...prev.slice(0, -1),
          {
            ...userMsg,
            id: slug(),
          },
        ]);
      },
    }),
    [chatbotName, placeholder, messages, blockId, user]
  );

  const blockDisplayStyle = useMemo(
    () =>
      buildBlockDisplayStyle({
        isInPopup,
        height: config.style?.height,
        heightValue: config.style?.heightValue,
      }),
    [config.style?.height, config.style?.heightValue, isInPopup]
  );

  return (
    <ConsumerProvider>
      <StyledChatbot $blockHeights={blockDisplayStyle} $isPopup={isInPopup}>
        <ScrollToBottom
          checkInterval={20}
          className={cn(
            "custom-scrollbar m-[30px_10px_0_30px] flex min-h-0 flex-col [&>button]:hidden [&>div]:flex [&>div]:w-full [&>div]:flex-col [&>div]:gap-5",
            {
              "h-screen": isInPopup,
              "h-auto": !isInPopup,
            }
          )}
        >
          <Messages
            config={config}
            onUsage={onUsage}
            messages={messages}
            setMessages={setMessages}
            liveBotResponse={liveBotResponse}
            isSubmitting={isSubmitting}
          />
        </ScrollToBottom>
        <UserPrompt
          {...userPromptProps}
          isDisabled={isDisabled}
          isInPopup={isInPopup}
          model={model}
          onUsage={onUsage}
          dataSources={dataSources}
          dataSourcesNoResultsMessage={dataSourcesNoResultsMessage}
          dataSourcesNoResultsMode={dataSourcesNoResultsMode}
        />
      </StyledChatbot>
    </ConsumerProvider>
  );
}

const Messages = ({
  config,
  onUsage,
  messages,
  setMessages,
  liveBotResponse,
  isSubmitting,
}: React.ComponentProps<typeof LiveChatbotPrompt> & {
  messages: Msg[];
  setMessages: React.Dispatch<React.SetStateAction<Msg[]>>;
  liveBotResponse: string;
  isSubmitting: boolean;
}) => {
  const chatbotSessionId = useChatbotSessionId();

  const {
    blockId,
    chatbotName,
    defaultMessage,
    defaultPrompt,
    directive,
    displayDisclosure,
    disclosureText,
    isStaticMode,
    model,
    triggers,
    user,
    dataSources,
    dataSourcesNoResultsMode,
    dataSourcesNoResultsMessage,
  } = useChatConfig(config);

  const triggerChatbotBlockAction = useTriggerChatbotBlockAction();

  const [isInitializing, setIsInitializing] = useState(false);

  const [runChatCompletion, resultRef, result] = useChatCompletion(
    model,
    onUsage
  );

  const [isAtBottom] = useAtBottom();
  const [isAtTop] = useAtTop();
  const [isScrollSticky] = useSticky();
  const scrollToBottom = useScrollToBottom();

  useEffect(() => {
    const systemMessages: Msg[] = [
      {
        id: slug(),
        role: "system",
        content: directive,
        message: isStaticMode ? defaultMessage : undefined,
        hidden: isStaticMode ? undefined : true,
      },
    ];
    if (dataSources.length > 0) {
      let msg = SYSTEM_MSG_DATASOURCE_AVAILABLE;
      if (dataSourcesNoResultsMode === "custom") {
        msg += generateSystemMsgNoResultsStatic(dataSourcesNoResultsMessage);
      }
      if (dataSourcesNoResultsMode === "chatgpt") {
        msg += SYSTEM_MSG_DATASOURCE_NO_RESULTS_GENERATE;
      }
      systemMessages.push({
        id: slug(),
        role: "system",
        content: msg,
        hidden: true,
      });
    }
    setMessages(
      isStaticMode
        ? [
            ...systemMessages,
            {
              id: slug(),
              role: "user",
              content: "",
            },
          ]
        : [
            ...systemMessages,
            {
              id: slug(),
              role: "user",
              content: defaultPrompt,
              hidden: true,
            },
            {
              id: slug(),
              role: "assistant",
              content: "",
            },
          ]
    );
  }, [
    defaultMessage,
    defaultPrompt,
    directive,
    isStaticMode,
    setMessages,
    dataSources,
    dataSourcesNoResultsMode,
    dataSourcesNoResultsMessage,
  ]);

  useEffect(() => {
    async function sayHello() {
      try {
        setIsInitializing(true);
        const { status } = await runChatCompletion({
          messages: messages.map((m) => {
            if (m.role === "function") {
              return {
                role: m.role,
                name: m.name,
                content: m.content,
              } as FuncMsg;
            } else {
              return {
                role: m.role,
                content: m.content ? m.content : "",
              };
            }
          }),
          blockId,
        });

        if (status === "complete") {
          setMessages((prev) => [
            ...prev.slice(0, -1),
            {
              id: slug(),
              role: "assistant",
              content: resultRef.current,
            },
            {
              id: slug(),
              role: "user",
              content: "",
            },
          ]);
        } else {
          sayHelloFallback();
        }
      } catch (error) {
        console.error(error);
        sayHelloFallback();
      } finally {
        setIsInitializing(false);
      }

      function sayHelloFallback() {
        setMessages([
          {
            id: slug(),
            role: "system",
            content: directive,
            message: defaultMessage,
          },
          {
            id: slug(),
            role: "user",
            content: "",
          },
        ]);
      }
    }

    if (
      !isStaticMode &&
      messages.filter((m) => m.role !== "system").length === 2 &&
      !isInitializing
    ) {
      sayHello();
    }
  }, [
    defaultMessage,
    directive,
    isInitializing,
    isStaticMode,
    blockId,
    resultRef,
    runChatCompletion,
    user,
    messages,
    setMessages,
  ]);

  return (
    <>
      {messages.map((msg, i, msgs) => {
        if (msg.hidden) {
          return null;
        }

        if (isBotMsg(msg)) {
          const marker = isStaticMode ? 1 : 3;
          const isBotResponse = i > marker && msg.role === "assistant";

          if (i === msgs.length - 1) {
            return (
              <BotMessage
                chatbotName={chatbotName}
                key={msg.id}
                displayDisclosure={displayDisclosure}
                disclosureText={disclosureText}
                isLoading
              >
                <BotResponse>{result}</BotResponse>
              </BotMessage>
            );
          }

          const prompt = isBotResponse ? msgs[i - 1].content : undefined;

          return (
            <BotMessage
              chatbotName={chatbotName}
              key={msg.id}
              displayDisclosure={displayDisclosure}
              disclosureText={disclosureText}
              source={msg.source}
              renderFeedback={
                isBotResponse
                  ? () => (
                      <BotFeedback
                        onFeedback={({ rating, comment }) =>
                          triggerChatbotBlockAction(
                            {
                              type: "ChatbotFeedback",
                              blockId,
                              chatbotSessionId,
                              values: {
                                rating,
                                comment,
                                // include the message that was rated
                                message: msg.content,
                                // include all the messages up to this point
                                conversation: messages.slice(0, i).map((m) => ({
                                  role: m.role,
                                  content: m.content,
                                })),
                              } as ChatbotFeedbackActionEvent,
                            },
                            "Thanks for the feedback!"
                          )
                        }
                      />
                    )
                  : undefined
              }
            >
              <BotResponse data-testid="final-bot-response">
                {msg.content && !msg.message ? (
                  <BotMarkdown>{msg.content}</BotMarkdown>
                ) : (
                  msg.message ?? msg.content
                )}
              </BotResponse>
              {isBotResponse && triggers.length > 0 && (
                <BotActions>
                  {triggers.map((trigger) =>
                    trigger.onClick === "copy" ? (
                      <CopyBotMessage
                        key={trigger.id}
                        buttonText={trigger.buttonText}
                        message={msg}
                        onClick={() => {
                          toast.success(trigger.notificationText);
                        }}
                      />
                    ) : (
                      <RunZapBotMessage
                        key={trigger.id}
                        buttonText={trigger.buttonText}
                        message={msg}
                        onClick={() => {
                          triggerChatbotBlockAction(
                            {
                              type: "ChatbotButtonClick",
                              blockActionId: trigger.id,
                              values: {
                                response: msg.content ? msg.content : "",
                                chatbotName,
                                prompt,
                              },
                            },
                            trigger.notificationText
                          );
                        }}
                      />
                    )
                  )}
                </BotActions>
              )}
            </BotMessage>
          );
        }

        return <UserMessage key={msg.id}>{msg.content}</UserMessage>;
      })}
      {isSubmitting && (
        <BotMessage
          chatbotName={chatbotName}
          displayDisclosure={displayDisclosure}
          disclosureText={disclosureText}
          isLoading
        >
          <BotResponse>
            <BotMarkdown>{liveBotResponse}</BotMarkdown>
          </BotResponse>
        </BotMessage>
      )}
      <StyledOverlay $scrollPosition={{ isAtTop, isAtBottom }} />
      <StyledJumpButtonWrapper>
        <StyledJumpButton
          $isAtBottom={isAtBottom}
          $isHidden={isScrollSticky}
          onClick={() => scrollToBottom()}
        >
          <Icon name="arrowBigDown" size={22} color="currentColor" />
          Jump to latest
        </StyledJumpButton>
      </StyledJumpButtonWrapper>
    </>
  );
};
